import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'features/rootReducer';
import qs, { ParsedQs } from 'qs';
import {
  ReferralMonthlyPayoutFilter,
  MonthlyPayoutModalType,
  MonthlyPayoutSortType,
  PayoutDetailModalInfo,
  SellerMonthlyPayout,
  SellerPayoutDetailInfo,
  SellerPayoutStatusInfo,
  SellerPayoutStatusParams,
  SellerStatusModalInfo,
  TotalPayoutDetailInfo,
  ReferralMonthlyPayoutSortType,
} from 'types/marketplace/revenue';
import { ExcelMultiDataSet } from 'types/react-export-excel';
import { monthlyPayoutDataToExcelMultiDataSet } from 'modules/excelTemplate/revenue/monthlyPayout';

export interface ReferralMonthlyPayoutState {
  filter: ReferralMonthlyPayoutFilter;
  loading: boolean;
  data?: SellerMonthlyPayout;
  error: boolean;
  excelLoading: boolean;
  excelData?: SellerMonthlyPayout;
  excelError: boolean;
  statusModal?: SellerStatusModalInfo;
  detailModal?: PayoutDetailModalInfo;
  sellerPayoutStatusInfo: {
    loading: boolean;
    data?: SellerPayoutStatusInfo;
    error?: boolean;
  };
  payoutDetailInfo: {
    loading: boolean;
    sellerData?: SellerPayoutDetailInfo;
    totalData?: TotalPayoutDetailInfo;
    error?: boolean;
  };
  confirmPayoutDetailInfo: {
    loading: boolean;
  };
  executePayoutDetailInfo: {
    loading: boolean;
  };
  paypalInfo: {
    loading: boolean;
    data?: any;
    error?: boolean;
  };
}

const initialState: ReferralMonthlyPayoutState = {
  filter: {
    year: new Date().getFullYear().toString(),
    sortType: ReferralMonthlyPayoutSortType.REVENUE_DESC,
    keyword: '',
    pageNo: 1,
  },
  loading: false,
  excelLoading: false,
  error: false,
  excelError: false,
  sellerPayoutStatusInfo: {
    loading: false,
  },
  payoutDetailInfo: {
    loading: false,
  },
  confirmPayoutDetailInfo: {
    loading: false,
  },
  executePayoutDetailInfo: {
    loading: false,
  },
  paypalInfo: {
    loading: false,
  },
};

export const referralMonthlyPayoutSlice = createSlice({
  name: 'referralMonthlyPayout',
  initialState: initialState,
  reducers: {
    resetFilter: state => {
      state.filter = initialState.filter;
    },
    search: (state, { payload }: PayloadAction<ReferralMonthlyPayoutFilter>) => {
      state.filter = payload;
    },
    pageChange: (state, { payload }: PayloadAction<number>) => {
      const pageNo = payload;
      if (!pageNo) {
        state.filter.pageNo = 1;
      }
      state.filter.pageNo = pageNo;
    },
    initialize: (state, { payload }: PayloadAction<ParsedQs>) => {
      const { year, keyword, sortType, pageNo } = payload;

      if (!year) {
        state.filter = initialState.filter;
      } else {
        state.filter = {
          year,
          keyword: keyword ?? '',
          sortType: sortType !== undefined ? Number(sortType) : ReferralMonthlyPayoutSortType.REVENUE_DESC,
          pageNo: pageNo ?? 1,
        } as ReferralMonthlyPayoutFilter;
      }
    },
    loadData: state => {
      state.loading = true;
      state.data = undefined;
    },
    loadDataSuccess: (state, { payload }: PayloadAction<SellerMonthlyPayout>) => {
      state.loading = false;
      state.data = payload;
      state.error = false;
    },
    loadDataFailure: state => {
      state.loading = false;
      state.data = undefined;
      state.error = true;
    },
    setSortType: (state, { payload }: PayloadAction<ReferralMonthlyPayoutSortType>) => {
      state.filter.sortType = payload;
    },
    openSellerStatus: (state, { payload }: PayloadAction<SellerStatusModalInfo>) => {
      state.statusModal = payload;
    },
    loadSellerPayoutStatus: state => {
      state.sellerPayoutStatusInfo = {
        loading: true,
      };
    },
    loadSellerPayoutStatusSuccess: (state, { payload }: PayloadAction<SellerPayoutStatusInfo>) => {
      state.sellerPayoutStatusInfo.loading = false;
      state.sellerPayoutStatusInfo.data = payload;
    },
    loadSellerPayoutStatusFailure: state => {
      state.sellerPayoutStatusInfo.loading = false;
      state.sellerPayoutStatusInfo.error = true;
    },
    closeSellerStatus: state => {
      state.statusModal = undefined;
      state.sellerPayoutStatusInfo = {
        loading: false,
      };
    },
    changeSellerStatus: (_, _action: PayloadAction<Omit<SellerPayoutStatusParams, 'userId'>>) => {},
    putSellerStatus: state => {
      state.sellerPayoutStatusInfo = {
        loading: true,
      };
    },
    openReferrerPayoutDetail: (
      state,
      { payload }: PayloadAction<{ userId: number; month: number }>,
    ) => {
      state.detailModal = {
        type: MonthlyPayoutModalType.USER,
        ...payload,
      };
    },
    openTotalPayoutDetail: (state, { payload }: PayloadAction<number>) => {
      state.detailModal = {
        type: MonthlyPayoutModalType.TOTAL,
        month: payload,
      };
    },
    closePayoutDetail: state => {
      state.detailModal = undefined;
    },
    confirmPayoutDetail: state => {
      state.confirmPayoutDetailInfo.loading = true;
    },
    confirmPayoutDetailSuccess: state => {
      state.confirmPayoutDetailInfo.loading = false;
    },
    confirmPayoutDetailFailure: state => {
      state.confirmPayoutDetailInfo.loading = false;
    },
    executePayoutDetail: state => {
      state.executePayoutDetailInfo.loading = true;
    },
    executePayoutDetailSuccess: state => {
      state.executePayoutDetailInfo.loading = false;
    },
    executePayoutDetailFailure: state => {
      state.executePayoutDetailInfo.loading = false;
    },
    resetPayoutDetail: state => {
      state.confirmPayoutDetailInfo.loading = true;
    },
    resetPayoutDetailSuccess: state => {
      state.confirmPayoutDetailInfo.loading = false;
    },
    resetPayoutDetailFailure: state => {
      state.confirmPayoutDetailInfo.loading = false;
    },
    loadSellerDetail: state => {
      state.payoutDetailInfo.loading = true;
      state.payoutDetailInfo.sellerData = undefined;
      state.payoutDetailInfo.totalData = undefined;
      state.payoutDetailInfo.error = undefined;
    },
    loadSellerDetailSuccess: (
      state,
      { payload }: PayloadAction<SellerPayoutDetailInfo | TotalPayoutDetailInfo>,
    ) => {
      state.payoutDetailInfo.loading = false;
      if (state.detailModal?.type === MonthlyPayoutModalType.USER) {
        state.payoutDetailInfo.sellerData = payload as SellerPayoutDetailInfo;
      } else {
        state.payoutDetailInfo.totalData = payload as TotalPayoutDetailInfo;
      }
    },
    loadSellerDetailFailure: state => {
      state.payoutDetailInfo.loading = false;
    },
    resetExcelData: state => {
      state.excelLoading = false;
      state.excelData = undefined;
    },
    loadExcelData: state => {
      state.excelLoading = true;
      state.excelData = undefined;
    },
    loadExcelDataSuccess: (state, { payload }: PayloadAction<SellerMonthlyPayout>) => {
      state.excelLoading = false;
      state.excelData = payload;
    },
    loadExcelDataFailure: state => {
      state.excelLoading = false;
      state.excelError = true;
    },
    resetPaypalData: state => {
      state.paypalInfo.loading = false;
      state.paypalInfo.data = undefined;
    },
    loadPaypalData: state => {
      state.paypalInfo.loading = true;
      state.paypalInfo.data = undefined;
    },
    loadPaypalDataSuccess: (state, { payload }: PayloadAction<SellerMonthlyPayout>) => {
      state.paypalInfo.loading = false;
      state.paypalInfo.data = payload;
    },
    loadPaypalDataFailure: state => {
      state.paypalInfo.loading = false;
      state.paypalInfo.error = true;
    },
  },
});

export const referralMonthlyPayoutActions = referralMonthlyPayoutSlice.actions;
export const referralMonthlyPayoutReducer = referralMonthlyPayoutSlice.reducer;

export const selectReferralMonthlyPayoutQueryString = (state: RootState) =>
  qs.stringify(state.referral.monthly.filter, { arrayFormat: 'repeat' });
export const selectReferralMonthlyPayoutAllQueryString = (state: RootState) => {
  const filter = state.referral.monthly.filter;
  return qs.stringify({ ...filter, pageNo: 1, pageSize: 999999999 }, { arrayFormat: 'repeat' });
};
export const selectReferralMonthlyPayoutSortType = (state: RootState) =>
  state.referral.monthly.filter.sortType;
export const selectReferralMonthlyPayoutFilter = (state: RootState) => state.referral.monthly.filter;
export const selectReferralMonthlyPayoutLoading = (state: RootState) => state.referral.monthly.loading;
export const selectReferralMonthlyPayoutError = (state: RootState) => state.referral.monthly.error;
export const selectReferralMonthlyPayoutData = (state: RootState) => state.referral.monthly.data;
export const selectReferralMonthlyPayoutExcelLoading = (state: RootState) =>
  state.referral.monthly.excelLoading;
export const selectReferralMonthlyPayoutExcelData = (state: RootState) => {
  const data = state.referral.monthly.excelData;
  return data ? monthlyPayoutDataToExcelMultiDataSet(data) : undefined;
};
export const selectReferralMonthlyPayoutExcelMultiDataSet = (
  state: RootState,
): ExcelMultiDataSet[] | undefined => {
  const data = state.referral.monthly.data;
  return data ? monthlyPayoutDataToExcelMultiDataSet(data) : undefined;
};

// Status Modal
export const selectReferralPayoutStatusEmail = (state: RootState): string =>
  state.referral.monthly.statusModal?.email ?? '';
export const selectReferralIsOpenedStatus = (state: RootState) =>
  state.referral.monthly.statusModal !== undefined;
export const selectReferralSellerStatusModal = (state: RootState) => state.referral.monthly.statusModal;
export const selectReferralSellerStatusInfo = (state: RootState) =>
  state.referral.monthly.sellerPayoutStatusInfo;

// Detail Modal
export const selectReferralIsOpenedDetail = (state: RootState) =>
  state.referral.monthly.detailModal !== undefined;
export const selectReferralPayoutDetailModal = (state: RootState) => state.referral.monthly.detailModal;
export const selectReferralPayoutDetailInfo = (state: RootState) => state.referral.monthly.payoutDetailInfo;
export const selectReferralConfirmPayoutDetailInfo = (state: RootState) =>
  state.referral.monthly.confirmPayoutDetailInfo;
export const selectReferralExecutePayoutDetailInfo = (state: RootState) =>
  state.referral.monthly.executePayoutDetailInfo;
export const selectReferralPaypalInfo = (state: RootState) => state.referral.monthly.paypalInfo;
